import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import { TASK_BET_TYPE, TASK_OUTCOME_REQUIRED } from "../../types/gamePass";
import type {
  TaskApiRecord,
  TaskBetType,
  TaskOutcomeRequired,
  UpdateTaskRequest,
  UpdateTaskResponse,
} from "../../types/gamePass";
import type {
  BetType,
  TaskDraft,
  TaskQualifier,
} from "../../components/GamePass/CreateSeason/createSeasonData";
import { betTypeRequiresLegCount } from "../../components/GamePass/CreateSeason/createSeasonData";
import { getPrivateInstance } from "../_privateInstance";
import {
  TASK_TYPE_TO_DATA_SOURCE,
  buildTaskRules,
  parseMinLegCount,
  parseOddsRequirement,
  parseXpReward,
  resolveCountingMethod,
  resolveMinBetAmount,
  resolveTargetValue,
  resolveTaskCompletionMode,
} from "./useCreateTask";
import { unwrapGamePassEntity } from "./responseMappers";

/** Build the path for `PUT /game-pass-admin/v2/admin/tasks/{task_id}` by
 *  substituting `{task_id}` in the centralised route template. The
 *  baseURL is supplied by the axios instance, so this returns just
 *  the path — matches the `API_ROUTES.X` call shape used everywhere
 *  else (see `useGetSeasonsList.ts`). */
const buildUpdateTaskUrl = (taskId: string): string =>
  API_ROUTES.UPDATE_TASK.replace("{task_id}", taskId);

/** Same qualifier → BE enum table as `useCreateTask.ts`. Re-declared
 *  locally (rather than imported) so this hook stays self-contained and
 *  a future relabel of either side surfaces a compile error on its own
 *  side only, instead of silently flipping the other. */
const QUALIFIER_TO_OUTCOME_REQUIRED: Record<TaskQualifier, TaskOutcomeRequired> =
  {
    WIN: TASK_OUTCOME_REQUIRED.WIN,
    WIN_OR_PUSH: TASK_OUTCOME_REQUIRED.WIN_OR_PUSH,
    ANY_SETTLED: TASK_OUTCOME_REQUIRED.ANY_SETTLED,
  };

/** Same bet-type → BE enum table as `useCreateTask.ts`. `"ANY"` collapses
 *  to `null` which the PUT endpoint interprets as "clear the bet-type
 *  restriction" per the clearable-field contract. */
const BET_TYPE_TO_API_BET_TYPE: Record<BetType, TaskBetType | null> = {
  ANY: null,
  SINGLE: TASK_BET_TYPE.SINGLE,
  PARLAY: TASK_BET_TYPE.PARLAY,
  SGP: TASK_BET_TYPE.SGP,
  LIVE: TASK_BET_TYPE.LIVE,
};

/**
 * Convert the UI's `TaskDraft` into the BE `UpdateTaskRequest` body.
 *
 * Unlike the create mapper this returns a **partial** payload — only
 * the fields the Create / Edit Task modal actually surfaces to the
 * admin. Sending anything else risks overwriting BE state the UI
 * doesn't model (e.g. a future `xp_reward` value set via a different
 * surface would be zeroed if we naively sent the constant we use on
 * create).
 *
 * What gets sent:
 *   - `name` / `description`   → free-text edits
 *   - `data_source`            → Task Type pill (SPORTSBOOK / SOCIALS / CASINO)
 *   - `counting_method`        → `"STREAK"` for a sportsbook Streak task
 *                                 (`resolveCountingMethod`), else `"COUNT"`.
 *                                 Sent on every update so removing the streak
 *                                 flips it back to COUNT.
 *   - `target_value`           → per task type (`resolveTargetValue`): Number
 *                                 of Bets (plain sportsbook COUNT — falling back
 *                                 to the preserved goal / fallback), streak days
 *                                 (sportsbook Streak), X Amount (socials) or
 *                                 Action Amount (casino); shares the create
 *                                 mapper's `NaN` fallback. `null` for casino when
 *                                 blank.
 *   - `min_bet_amount`         → sportsbook "Minimum Bet Amount" OR casino
 *                                 "Wager Amount" input (via
 *                                 `resolveMinBetAmount`; clearable `null`).
 *                                 Top-level column, NOT a rule.
 *   - `outcome_required`       → Qualifier pill (clearable: `null`
 *                                 if no pick — the modal's submit gate
 *                                 prevents this in practice)
 *   - `bet_type`               → Type of Bet pill (clearable: the
 *                                 default `"ANY"` pill emits `null`
 *                                 which is the BE's "clear the
 *                                 restriction" signal)
 *   - `min_odds_american`      → Minimum Odds input (clearable: `null`
 *                                 when the admin clears the field so the
 *                                 stored value is removed; American odds,
 *                                 may be negative). Also emitted as an
 *                                 `odds_american gte` rule via `buildTaskRules`.
 *   - Maximum Odds input       → NOT a top-level column; ships exclusively as
 *                                 an `odds_american lte` rule via
 *                                 `buildTaskRules` (cleared by rebuilding
 *                                 `rules` without that entry).
 *   - `rules`                  → all trigger selections (Sport Specific tree;
 *                                 casino provider / game pickers; casino
 *                                 Action; casino Bet Currency), encoded as
 *                                 `sport_id` / `provider` / `game_id` /
 *                                 `outcome` / `money_type` rules (Game Pass V2)
 *                                 via `buildTaskRules`. `[]` when nothing is
 *                                 selected, which clears every rule on the BE.
 *                                 Replaces the old top-level `sports` /
 *                                 `casino_providers` / `casino_games` fields.
 *                                 (The casino Action Amount ships on
 *                                 `target_value`, and the Minimum Bet Amount on
 *                                 `min_bet_amount` — neither is a rule.)
 *   - `xp_reward`              → XP Amount input (floored integer; `0`
 *                                 when blank / invalid)
 *   - `min_leg_count`          → Number of Legs input, shown for the multi-leg
 *                                 bet types (Parlay / SGP). Clearable: `null`
 *                                 for bet types that don't use a leg count or
 *                                 a blank/invalid value.
 *
 * What is intentionally NOT sent:
 *   - `season_id` / `challenge_id` / `challenge` / `challenge_order`:
 *     identity fields. The BE rejects attempts to move a task across
 *     challenges via PUT (per the spec, `UpdateTaskRequest` doesn't
 *     even carry them).
 *   - `path` / `completion_condition` /
 *     `exclude_rebet_tokens` / `is_active`: constants the UI doesn't model.
 *     Keeping them out of the body preserves whatever the BE has stored.
 *   - `completion_mode`: sent for sportsbook / casino tasks — maps the
 *     submission-mode radio to `OPEN` (Auto) or `USER_CHOICE` (Manual).
 *   - `trigger.minBetCurrency`: collected by the modal but UI-only today —
 *     there's no BE rule for it yet, so it isn't shipped. When the BE adds
 *     support, encode it in `buildTaskRules` (mirrors the create mapper).
 */
export const mapTaskDraftToUpdateTaskRequest = (
  draft: TaskDraft,
  /** S3 object key for the task image, resolved by the caller. Three states
   *  drive the wire payload:
   *  - `undefined` → omit `img_key` entirely (edit didn't touch the image, so
   *    the stored value is left untouched).
   *  - a non-empty string → a newly-uploaded image (sent as-is).
   *  - `null` → the admin explicitly removed the image (sent as `img_key: null`
   *    so the backend clears it). */
  imgKey?: string | null,
): UpdateTaskRequest => {
  const trigger = draft.trigger;
  const completionMode = resolveTaskCompletionMode(draft);
  // Compare the UI Task Type pill value, not the wire `data_source` — the
  // socials pill is `"SOCIALS"` while the wire value is `"SOCIAL"`.
  const isSportsbook = trigger.taskType === "SPORTSBOOK";
  return {
    name: draft.name.trim(),
    description: draft.description.trim(),
    ...(imgKey !== undefined ? { img_key: imgKey } : {}),
    // Socials pill (`"SOCIALS"`) → singular wire value (`"SOCIAL"`); see
    // `TASK_TYPE_TO_DATA_SOURCE`. Shipping the pill value verbatim 422s.
    data_source: TASK_TYPE_TO_DATA_SOURCE[trigger.taskType],
    // `"STREAK"` for a sportsbook Streak task (the BE drives streak logic off
    // `counting_method` + `target_value`); `"COUNT"` otherwise. Sent on every
    // update so removing the streak on edit flips the method back to COUNT.
    counting_method: resolveCountingMethod(draft),
    // Per-task-type goal (see `resolveTargetValue`): casino → Action Amount;
    // socials → "X Amount" milestone; sportsbook Streak → number of streak
    // days; sportsbook (default) → Number of Bets input (or preserved goal /
    // fallback).
    target_value: resolveTargetValue(draft),
    // Sportsbook-specific fields. Gated on `isSportsbook` so casino AND social
    // tasks always send `null` — even if a qualifier / bet-type / odds value
    // was filled in before the admin switched the Task Type pill away from
    // Sportsbook (those draft values are never cleared on switch).
    outcome_required:
      isSportsbook && trigger.qualifier
        ? QUALIFIER_TO_OUTCOME_REQUIRED[trigger.qualifier]
        : null,
    bet_type: isSportsbook ? BET_TYPE_TO_API_BET_TYPE[trigger.betType] : null,
    min_odds_american: isSportsbook
      ? parseOddsRequirement(trigger.oddsRequirement)
      : null,
    // Maximum Odds is NOT a top-level column — it ships exclusively as an
    // `odds_american` `lte` rule via `buildTaskRules`. Because `rules` is always
    // rebuilt wholesale on update, a cleared Maximum Odds (no `lte` rule) clears
    // the stored requirement.
    // Number of Legs is Parlay-only (and sportsbook-only) — clear it for a
    // casino / social task or any other bet type, even if a stale value
    // lingers after a Task Type / bet-type switch.
    min_leg_count:
      isSportsbook && betTypeRequiresLegCount(trigger.betType)
        ? parseMinLegCount(trigger.minLegCount)
        : null,
    // Top-level `min_bet_amount` column (NOT a rule). Sourced per task type by
    // `resolveMinBetAmount`: the sportsbook "Minimum Bet Amount" or the casino
    // "Wager Amount". Clearable: `null` for social tasks or a blank / invalid
    // value, which removes any stored minimum on the BE.
    min_bet_amount: resolveMinBetAmount(draft),
    xp_reward: parseXpReward(trigger.xpAmount),
    // All trigger selections — Sport Specific, casino provider / game, casino
    // Action (outcome) and casino Bet Currency (money_type) — are sent as
    // condition rules (Game Pass V2) instead of top-level columns. (The casino
    // Action Amount ships on `target_value`, and the Minimum Bet Amount on
    // `min_bet_amount`, above — neither is a rule.)
    // `buildTaskRules` gates by task type and returns `[]` when nothing is
    // selected, which the BE treats as "clear every rule" — so a stale
    // selection from a Task Type switch is cleared rather than left dangling.
    //
    // DELIBERATE V2 CONTRACT: the PAM UI owns the *entire* rule set on edit.
    // `rules` is always sent (rebuilt wholesale from the draft), so any rule
    // the BE has stored that this UI does not yet reconstruct is intentionally
    // cleared by an edit. Today `buildTaskRules` only emits the rule fields
    // above; if/when the BE adds a rule the modal doesn't model yet, it MUST be
    // added to `buildTaskRules` or it will be silently wiped on the next save.
    // (Streak is NOT a rule — it drives `counting_method` + `target_value`.)
    // This
    // full-overwrite behaviour is coordinated with the BE V2 contract; do not
    // switch it to a partial/merge update without re-confirming with the BE.
    rules: buildTaskRules(draft),
    ...(completionMode !== undefined
      ? { completion_mode: completionMode }
      : {}),
    // NOTE: `trigger.streak` now drives `counting_method` + `target_value`
    // (above). `trigger.minBetCurrency` remains intentionally omitted — UI-only
    // today (no BE rule for it yet). See the "intentionally NOT sent" note.
  };
};

/** Variables fed to `useUpdateTask.mutateAsync`. Bundled together so
 *  callers don't have to remember the order of positional args — and so
 *  the React Query mutation typing stays a single object. */
export type UpdateTaskVariables = {
  taskId: string;
  body: UpdateTaskRequest;
  /** Optional — used purely to invalidate the parent season's detail
   *  query on success. When omitted (callers that don't know the
   *  season-id context), only the list query is invalidated. */
  seasonId?: string | null;
};

const putUpdateTask = async ({
  taskId,
  body,
}: UpdateTaskVariables): Promise<TaskApiRecord> => {
  // `getPrivateInstance()` already bakes the baseURL + bearer token
  // into the axios instance (via the request interceptor in
  // `PrivateAdminPanelInstance.js`), so the call site stays as terse
  // as `useGetSeasonsList.ts`: pass the route + body and let the
  // instance do the rest.
  const response = await getPrivateInstance().put<UpdateTaskResponse>(
    buildUpdateTaskUrl(taskId),
    // Same no-`operator_id` rationale as `useCreateTask.ts` — the
    // `gp_tasks` table has no operator column. The body is shipped
    // partial and as-is.
    body,
  );
  return unwrapGamePassEntity(response.data, "task");
};

/**
 * React Query mutation for `PUT /game-pass-admin/v2/admin/tasks/{task_id}`.
 *
 * On success invalidates the parent season's detail query (so any
 * surface that reads the season's nested task list refetches) and the
 * top-level seasons list (so the per-card `task_count` metric stays in
 * sync if the BE ever recomputes it on every task mutation).
 */
export const useUpdateTask = (): UseMutationResult<
  TaskApiRecord,
  Error,
  UpdateTaskVariables
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["game-pass", "tasks", "update"],
    mutationFn: putUpdateTask,
    onSuccess: (_task, variables) => {
      if (variables.seasonId) {
        // Keep the in-place Season Details editor in sync with the saved
        // task so the challenge card's Tasks dropdown reflects the edit.
        queryClient.invalidateQueries({
          queryKey: ["game-pass", "tasks", "list", variables.seasonId],
        });
        queryClient.invalidateQueries({
          queryKey: ["game-pass", "seasons", "detail", variables.seasonId],
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: ["game-pass", "tasks", "list"],
        });
      }
      queryClient.invalidateQueries({
        queryKey: ["game-pass", "seasons", "list"],
      });
    },
  });
};
